import { createStore, useStore } from 'zustand';
import {
  createTutorialStoreSlice,
  TutorialStep,
  TutorialStoreValue,
} from './tutorial-store';
import { createJSONStorage, persist } from 'zustand/middleware';
import { SUPERSTAR_DETAILS_TUTORIAL_TARGET_IDS } from '@wwe-portal/web/utils/superstar-details/constants';
import { STORAGE_ITEM_SUPERSTAR_TUTORIAL } from '@wwe-portal/web/utils/constants/keys';

interface SuperstarTutorialStoreValue extends TutorialStoreValue {
  isViewed: boolean;
  finish: () => void;
}

const createSuperstarTutorialStore = () => {
  const persistInitiator = persist<
    SuperstarTutorialStoreValue,
    [],
    [],
    Pick<SuperstarTutorialStoreValue, 'isViewed'>
  >(
    (set, get, ...rest) => {
      const createBaseSlice = createTutorialStoreSlice(steps);
      const baseSlice = createBaseSlice(set, get, ...rest);
      return {
        ...baseSlice,
        isViewed: false,
        start: async () => {
          await baseSlice.start();
          set({ isViewed: true });
        },
        finish: () => {
          set({ isViewed: true, currentStep: undefined });
        },
      };
    },
    {
      name: STORAGE_ITEM_SUPERSTAR_TUTORIAL,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isViewed: state.isViewed,
      }),
    }
  );
  return createStore(persistInitiator);
};

let _storeApi: ReturnType<typeof createSuperstarTutorialStore>;

const getSuperstarTutorialStore = () => {
  _storeApi ??= createSuperstarTutorialStore();
  return _storeApi;
};

export const useSuperstarTutorialStore = <T>(
  selector: (state: SuperstarTutorialStoreValue) => T
) => {
  return useStore(getSuperstarTutorialStore(), selector);
};

export const SUPERSTAR_DETAILS_STEP_IDS = {
  WELCOME: '1-welcome',
  SLIDE: '2.1-slide',
  SLIDING: '2.2-sliding',
  COMPARE: '3-compare',
  REBASE: '4-rebase',
  FINISH: '5-finish',
};

const steps: TutorialStep[] = [
  {
    id: SUPERSTAR_DETAILS_STEP_IDS.WELCOME,
    nextId: SUPERSTAR_DETAILS_STEP_IDS.SLIDE,
    targetId: SUPERSTAR_DETAILS_TUTORIAL_TARGET_IDS.SET_BASE,
  },
  {
    id: SUPERSTAR_DETAILS_STEP_IDS.SLIDE,
    nextId: SUPERSTAR_DETAILS_STEP_IDS.SLIDING,
    targetId: SUPERSTAR_DETAILS_TUTORIAL_TARGET_IDS.SLIDER,
  },
  {
    id: SUPERSTAR_DETAILS_STEP_IDS.SLIDING,
    nextId: SUPERSTAR_DETAILS_STEP_IDS.COMPARE,
    targetId: SUPERSTAR_DETAILS_TUTORIAL_TARGET_IDS.SUPERSTAR_LEVEL_SECTION,
    highlightTargetIds: [SUPERSTAR_DETAILS_TUTORIAL_TARGET_IDS.TOTAL_SCORE],
  },
  {
    id: SUPERSTAR_DETAILS_STEP_IDS.COMPARE,
    nextId: SUPERSTAR_DETAILS_STEP_IDS.REBASE,
    targetId: SUPERSTAR_DETAILS_TUTORIAL_TARGET_IDS.SUPERSTAR_LEVEL_SECTION,
    highlightTargetIds: [SUPERSTAR_DETAILS_TUTORIAL_TARGET_IDS.TOTAL_SCORE],
  },
  {
    id: SUPERSTAR_DETAILS_STEP_IDS.REBASE,
    nextId: SUPERSTAR_DETAILS_STEP_IDS.FINISH,
    targetId: SUPERSTAR_DETAILS_TUTORIAL_TARGET_IDS.SET_BASE,
  },
  {
    id: SUPERSTAR_DETAILS_STEP_IDS.FINISH,
    targetId: undefined,
  },
];
